// Сводка недели для Week View: работа → люди × дни и человек → работы × дни.
// Все часы недели, не только выбранные в сетке работы: экран «посмотреть всё».
// У человека в один день на одной работе бывает несколько записей (разные
// авторы) — складываем, а не берём первую, как экран подписи.

/**
 * @param {{ jobs: object[], employees: object[], weekRecords: object[], weekDates: string[],
 *           jobLabel: (job) => {code: string, name: string, customer: string},
 *           employeeName: (emp) => string }} p
 */
export function buildWeekView({ jobs, employees, weekRecords, weekDates, jobLabel, employeeName }) {
    const jobById = new Map(jobs.map(j => [j.id, j]));
    const empById = new Map(employees.map(e => [e.id, e]));
    const empty = () => Object.fromEntries(weekDates.map(d => [d, 0]));
    const inWeek = new Set(weekDates);

    const byJob = new Map();
    const byEmp = new Map();
    const dayTotals = empty();
    let total = 0;

    for (const r of weekRecords) {
        if (r.isDeleted || !inWeek.has(r.workDate)) continue;
        const h = Number(r.hours) || 0;
        if (h <= 0 && !r.attendCode) continue;
        const job = jobById.get(r.jobId);
        const emp = empById.get(r.employeeId);
        const jl = job ? jobLabel(job) : { code: String(r.jobCode || r.jobId), name: '', customer: '' };
        const en = emp ? employeeName(emp) : `#${r.employeeId}`;

        if (!byJob.has(r.jobId)) byJob.set(r.jobId, { id: r.jobId, ...jl, people: new Map(), days: empty(), total: 0 });
        const j = byJob.get(r.jobId);
        if (!j.people.has(r.employeeId)) j.people.set(r.employeeId, { id: r.employeeId, name: en, days: empty(), codes: {}, total: 0 });
        const jp = j.people.get(r.employeeId);

        if (!byEmp.has(r.employeeId)) byEmp.set(r.employeeId, { id: r.employeeId, name: en, jobs: new Map(), days: empty(), codes: {}, total: 0 });
        const e = byEmp.get(r.employeeId);
        if (!e.jobs.has(r.jobId)) e.jobs.set(r.jobId, { id: r.jobId, ...jl, days: empty(), codes: {}, total: 0 });
        const ej = e.jobs.get(r.jobId);

        for (const acc of [j, jp, e, ej]) {
            acc.days[r.workDate] += h;
            acc.total += h;
        }
        if (r.attendCode) {
            jp.codes[r.workDate] = r.attendCode;
            ej.codes[r.workDate] = r.attendCode;
            e.codes[r.workDate] = r.attendCode;
        }
        dayTotals[r.workDate] += h;
        total += h;
    }

    const byName = (a, b) => a.name.localeCompare(b.name);
    const byCode = (a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true });
    const round = x => Math.round(x * 100) / 100;
    const fin = o => ({ ...o, total: round(o.total), days: Object.fromEntries(Object.entries(o.days).map(([d, v]) => [d, round(v)])) });

    return {
        weekDates,
        total: round(total),
        dayTotals: fin({ days: dayTotals, total: 0 }).days,
        jobs: [...byJob.values()].map(j => ({ ...fin(j), people: [...j.people.values()].map(fin).sort(byName) })).sort(byCode),
        employees: [...byEmp.values()].map(e => ({ ...fin(e), jobs: [...e.jobs.values()].map(fin).sort(byCode) })).sort(byName),
    };
}

// Те же сокращения, что в легенде сетки и в Week Control.
export const ATTEND_SHORT = { 'No Call No Show': 'NC', Excused: 'EX', Unexcused: 'UN', 'Termed - ADP entered': 'TM', 'Night work': 'NW' };
